import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
// Main application entry point
import { FocusStyleManager } from '@blueprintjs/core';
import { getQueryClient, initializeQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { WithAuth } from './auth';
import { Actions as AuthActions } from './auth/actions';
import configureStore from './configureStore';
import { initializeGlobals } from './globals';
import { accountScopedQueryKeyHash } from './lib/accountScopedQueryKeyHash';
import Root from './Root';
import { initializeSettings } from './settings/service';
import { IconProvider } from './shared/components';

import '@launchpad-ui/tokens/src/generated/fonts.css';
import '@launchpad-ui/tokens/src/generated/index.css';
import '@launchpad-ui/tokens/src/generated/media-queries.css';
import '@launchpad-ui/tokens/src/generated/themes.css';
import './global.scss';

const store = configureStore();

// Start auth check immediately so it resolves before React mounts.
// WithAuth's useEffect also dispatches this, but by then the epic has
// already completed and SIGNED_IN is in the store.
store.dispatch(AuthActions.checkingAuth());

const c = initializeSettings(store);

FocusStyleManager.onlyShowFocusOnTabs();

// Initialize QueryClient for gonfalon billing components with account-scoped caching
initializeQueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, _error: unknown) => {
        // Simple retry logic for vitalstatistix
        if (failureCount < 2) {
          return true;
        }
        return false;
      },
      refetchOnWindowFocus: false,
      queryKeyHashFn: accountScopedQueryKeyHash,
    },
    mutations: {
      retry: 0,
    },
  },
});

// LD client initialization is non-blocking — the ready/failed handlers in
// settings/service.ts will dispatch flag updates when the client is ready.
c.waitForInitialization(10).catch((err: unknown) => {
  console.warn('[Vitalstatistix] LaunchDarkly client failed to initialize. Continuing with defaults.', err);
});

// Only block on initializeGlobals (sets up window.ldConstants and OpenAPI middleware)
initializeGlobals()
  .then(() => {
    const container = document.getElementById('root');
    if (!container) {
      throw new Error('Failed to find the root element');
    }

    // Check if we need to restore a path from spa-redirect
    let restorePath = sessionStorage.getItem('spa-restore-path');
    if (restorePath) {
      sessionStorage.removeItem('spa-restore-path');
      // Sanitize restorePath: remove any /index.html from it (malformed from old redirects)
      restorePath = restorePath.replace(/\/index\.html/g, '');
      // Use history.replaceState to update the URL without triggering a navigation
      // Strip /index.html and any trailing slash to avoid double slashes
      const basePath = window.location.pathname.replace(/\/index\.html$/, '').replace(/\/$/, '');
      const newUrl = `${window.location.protocol}//${window.location.host}${basePath}${restorePath}`;
      window.history.replaceState(null, '', newUrl);
    }

    const root = createRoot(container);
    const queryClient = getQueryClient();

    // Check DevTools flag once at initialization (not on every render)
    const shouldShowDevtools = __DEV__ && store.getState().settings.enableReactQueryDevtools;

    root.render(
      <StrictMode>
        {/* oxlint-disable-next-line typescript/no-explicit-any -- Required for Redux store type compatibility */}
        <Provider store={store as any}>
          <QueryClientProvider client={queryClient}>
            <WithAuth>
              {({ user, isUserReady, onSignIn, onSignOut }) => (
                <IconProvider>
                  <Root user={user} isUserReady={isUserReady} onSignIn={onSignIn} onSignOut={onSignOut} />
                </IconProvider>
              )}
            </WithAuth>
            {shouldShowDevtools && <ReactQueryDevtools initialIsOpen={false} />}
          </QueryClientProvider>
        </Provider>
      </StrictMode>,
    );
  })
  .catch((error) => {
    console.error('Failed to initialize app:', error);
    const container = document.getElementById('root');
    if (container) {
      const root = createRoot(container);
      const errorMessage = error instanceof Error ? error.message : String(error);
      root.render(<div style={{ padding: '20px', color: 'red' }}>Failed to initialize app: {errorMessage}</div>);
    }
  });
